import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CHIP = {
  1: "bg-[#F5C451]/15 text-[#F5C451] border-[#F5C451]/40",
  2: "bg-white/10 text-ink border-white/25",
  3: "bg-[#CE8946]/15 text-[#CE8946] border-[#CE8946]/40",
};

// Who brings the most friends this month — the top three get a free Premium month.
export const ReferralBoard = () => {
  const { user } = useAuth();
  const [board, setBoard] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/referrals/leaderboard").then(({ data }) => setBoard(data))
      .catch((e) => console.error("could not load the sharers' board", e));
  }, [user]);

  if (!user || !board) return null;

  return (
    <div className="rounded-3xl border border-white/10 bg-panel p-8" data-testid="referral-board">
      <h2 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight">
        <Trophy className="h-5 w-5 text-brand" /> This month's sharers
      </h2>
      <p className="mt-1 text-sm text-ink-soft">
        The top {board.winners} people who bring friends in {board.month} each get{" "}
        {board.prize_days} free days of Premium.
      </p>

      {board.top.length === 0 ? (
        <p className="mt-5 text-sm text-ink-soft" data-testid="referral-board-empty">
          Nobody has brought a friend yet this month — one invite would put you first.
        </p>
      ) : (
        <ul className="mt-5 space-y-2">
          {board.top.map((row) => (
            <li
              key={row.user_id}
              data-testid={`sharer-${row.rank}`}
              className={`flex items-center gap-3 rounded-2xl border px-4 py-2.5 text-sm ${
                row.is_me ? "border-brand/50 bg-brand/10" : "border-white/8 bg-page"}`}
            >
              <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-bold ${
                CHIP[row.rank] || "border-white/12 text-ink-soft"}`}>
                {row.rank}
              </span>
              <span className="truncate">
                {row.is_me ? "You" : row.name}
                {row.won ? (
                  <Crown className="ms-1.5 inline h-3.5 w-3.5 text-[#F5C451]"
                         data-testid={`sharer-won-${row.rank}`} />
                ) : null}
              </span>
              <span className="ms-auto shrink-0 tabular-nums text-ink-soft">
                {row.friends} friend{row.friends === 1 ? "" : "s"}
                {row.in_the_money ? " · in the money" : ""}
              </span>
            </li>
          ))}
        </ul>
      )}

      <Link
        to="/hall-of-fame"
        data-testid="referral-board-hall"
        className="mt-4 inline-block text-sm text-brand underline-offset-4 hover:underline"
      >
        See everyone who has won a free month →
      </Link>

      {board.me && !board.me.rank && (
        <p className="mt-4 text-sm text-ink-soft" data-testid="referral-board-me">
          You have brought {board.me.friends} friend{board.me.friends === 1 ? "" : "s"} this month —{" "}
          {board.to_beat} would put you on the board.
        </p>
      )}
    </div>
  );
};
